import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";

export function labelPrintStatus(label, statuses) {
  const record = statuses[label.key];
  if (!record) return "never";
  return record.revisions.includes(label.request_revision) ? "printed" : "changed";
}

export const PRINT_STATUS_TEXT = {
  never: "No confirmed print", changed: "Changed since printing", printed: "Printed",
};

function validBatch(data, year, listType) {
  return data?.year === Number(year) && data.list_type === listType &&
    typeof data.id === "string" && /^[a-f0-9]{32}$/.test(data.id) &&
    Number.isInteger(data.request_count) && data.request_count > 0 &&
    Number.isInteger(data.label_count) && data.label_count >= data.request_count &&
    Number.isInteger(data.sheet_count) && data.sheet_count > 0 &&
    Array.isArray(data.recipients) && data.recipients.length === data.request_count &&
    data.recipients.every(r => typeof r.household_id === "string" && typeof r.member_id === "string") &&
    data.settings && typeof data.settings.product === "string";
}

function validHistory(data, year, listType, page) {
  return data?.year === Number(year) && data.list_type === listType && data.page === page &&
    typeof data.can_record === "boolean" && typeof data.has_more === "boolean" &&
    data.statuses && typeof data.statuses === "object" && !Array.isArray(data.statuses) &&
    Object.values(data.statuses).every(s => Array.isArray(s.revisions) &&
      s.revisions.every(r => typeof r === "string" && /^[a-f0-9]{64}$/.test(r))) &&
    Array.isArray(data.history) && data.history.every(b => validBatch(b, year, listType) &&
      typeof b.confirmed_at === "string" && typeof b.staff === "string");
}

export default function useLabelPrintHistory(year, listType) {
  const scope = `${year}|${listType}`;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const generation = useRef(0);
  const busyRef = useRef(false);
  const [page, setPage] = useState(0);
  const [retry, setRetry] = useState(0);
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState("");
  const [job, setJob] = useState(null);
  const ready = state?.scope === scope ? state.data : null;
  const pending = job?.scope === scope ? job : null;

  useEffect(() => {
    generation.current += 1;
    busyRef.current = false;
    setPage(0); setJob(null); setBusy(""); setActionError(""); setMessage("");
    return () => { generation.current += 1; };
  }, [scope]);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true); setError("");
    api.get("/sharing-tree/label-print-history", {
      params: { year, list_type: listType, page }, signal: ctrl.signal,
    }).then(({ data }) => {
      if (ctrl.signal.aborted || scopeRef.current !== scope) return;
      if (!validHistory(data, year, listType, page)) throw new Error("Print history could not be verified. Reload history.");
      setState({ scope, data }); setLoading(false);
    }).catch(e => {
      if (ctrl.signal.aborted || scopeRef.current !== scope) return;
      setError(formatApiError(e)); setLoading(false);
    });
    return () => ctrl.abort();
  }, [year, listType, scope, page, retry]);

  useEffect(() => {
    const refresh = () => setRetry(n => n + 1);
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, []);

  const refresh = () => setRetry(n => n + 1);
  const active = token => generation.current === token && scopeRef.current === scope;
  async function prepare(payload, labelCount, sheetCount) {
    if (busyRef.current || pending || !ready?.can_record || loading || error) return null;
    const token = generation.current;
    busyRef.current = true; setBusy("preparing"); setActionError(""); setMessage("");
    try {
      const { data } = await api.post("/sharing-tree/label-print-jobs", payload);
      if (!active(token)) return null;
      if (!validBatch(data, year, listType) || data.label_count !== labelCount ||
          data.sheet_count !== sheetCount || data.request_count !== payload.requests.length ||
          data.recipients.some((r, i) => r.household_id !== payload.requests[i].household_id ||
            r.member_id !== payload.requests[i].member_id)) {
        throw new Error("The prepared batch did not match the preview. Reload labels before printing.");
      }
      const next = { ...data, scope, attempted: false };
      setJob(next);
      return next;
    } catch (e) {
      if (active(token)) setActionError(formatApiError(e));
      return null;
    } finally {
      if (active(token)) { busyRef.current = false; setBusy(""); }
    }
  }
  function offered(id) {
    setJob(j => j?.id === id && j.scope === scope ? { ...j, attempted: true } : j);
  }
  function discard(reason = "Not recorded. These requests have not been marked as printed.") {
    if (busyRef.current) return;
    if (pending?.confirmationAttempted) {
      reason = "The confirmation result was uncertain. Reloading shared history; this batch may already be recorded.";
      refresh();
    }
    setJob(null); setActionError(""); setMessage(reason);
  }
  async function confirm() {
    if (!pending?.attempted || busyRef.current) return;
    const token = generation.current;
    const batchId = pending.id;
    busyRef.current = true; setBusy("confirming"); setActionError("");
    setJob(j => j?.id === batchId ? { ...j, confirmationAttempted: true } : j);
    try {
      const { data } = await api.post(`/sharing-tree/label-print-jobs/${batchId}/confirm`);
      if (!active(token)) return;
      if (!validBatch(data, year, listType) || data.id !== batchId || typeof data.confirmed_at !== "string") {
        throw new Error("The confirmation could not be verified. Retry this same batch.");
      }
      setJob(null); setMessage("Printing recorded. This batch is now in the shared history.");
      setPage(0); refresh();
      return data;
    } catch (e) {
      if (active(token)) setActionError(`Printing has not been confirmed here. ${formatApiError(e)} Retry this same batch to avoid duplicates.`);
    } finally {
      if (active(token)) { busyRef.current = false; setBusy(""); }
    }
  }
  return {
    statuses: ready?.statuses || {}, history: ready?.history || [],
    canRecord: !!ready?.can_record, loading: loading || (!ready && !error), error, actionError, message,
    page: ready?.page ?? 0, hasMore: !!ready?.has_more, setPage, refresh,
    pending, busy, prepare, offered, confirm, discard,
  };
}
